import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, CalendarClock, Clock } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { type Crumb } from "@/components/Breadcrumbs";
import { GUIDES, type GuideMeta } from "@/lib/guides";

export type GuideTocItem = { id: string; label: string };

export type GuideNextRead = {
  to: string;
  label: string;
  desc: string;
};

export type GuideLayoutProps = {
  guide: GuideMeta;
  /** Section headings the article will render; emits a TOC when 4+ items. */
  toc?: GuideTocItem[];
  children: ReactNode;
  /** 2–3 onward links rendered as a single "Next read" strip at the bottom. */
  nextRead?: GuideNextRead[];
};

/**
 * Article shell used by the rewritten guides. Sits inside the standard
 * site chrome (ToolLayout) with a byline strip under the header, the whole
 * article body in the #mv-content block, an optional TOC sidebar (4+
 * sections, lg and up) and the "Next read" / "More guides" links after
 * the content block.
 */
export function GuideLayout({ guide, toc, children, nextRead }: GuideLayoutProps) {
  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "Barcode Guides", path: "/barcode-guides" },
    { name: guide.shortTitle, path: guide.path },
  ];
  const showToc = (toc?.length ?? 0) >= 4;
  const updated = formatDate(guide.lastUpdated);

  return (
    <ToolLayout
      eyebrow="Guide"
      title={guide.title}
      description={guide.intro ?? guide.dek}
      crumbs={crumbs}
      wide={showToc}
      content={children}
      aside={
        showToc ? (
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              On this page
            </div>
            <nav aria-label="Article sections" className="mt-3 flex flex-col gap-2 text-sm">
              {toc!.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>
        ) : undefined
      }
      related={
        <>
          {nextRead && nextRead.length > 0 && <NextReadStrip items={nextRead} />}
          <RelatedGuidesStrip currentSlug={guide.slug} />
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="h-3.5 w-3.5" aria-hidden /> By ReadBarcode
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" aria-hidden /> {guide.readingMinutes} min read
        </span>
        {updated && (
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden /> Published {updated}
          </span>
        )}
      </div>
    </ToolLayout>
  );
}

function NextReadStrip({ items }: { items: GuideNextRead[] }) {
  return (
    <section
      aria-label="Next read"
      className="mt-16 rounded-lg border border-border bg-muted/30 p-5"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Next Read
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="group flex items-start justify-between gap-3 rounded-md border border-border bg-background p-4 transition hover:border-primary/50"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{it.label}</div>
              <p className="mt-1 text-sm text-muted-foreground">{it.desc}</p>
            </div>
            <ArrowRight
              className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
              aria-hidden
            />
          </Link>
        ))}
      </div>
    </section>
  );
}

function RelatedGuidesStrip({ currentSlug }: { currentSlug: string }) {
  const others = GUIDES.filter((g) => g.slug !== currentSlug).slice(0, 4);
  if (others.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        More Guides
      </h2>
      <ul className="mt-3 flex flex-wrap gap-2">
        {others.map((g) => (
          <li key={g.slug}>
            <Link
              to={g.path}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground transition hover:border-primary/50 hover:bg-muted/40"
            >
              {g.shortTitle}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}

/** Article H2 with the anchor id consumed by the TOC, followed by its body.
 *  Renders flat (no wrapper element) because it sits directly inside
 *  #mv-content; the body must be p, h3, ul, ol, figure or table blocks. */
export function GuideArticleSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <>
      <h2 id={id}>{title}</h2>
      {children}
    </>
  );
}

/** "Key takeaways" list, typically placed at the end of the article body.
 *  The label is a styled paragraph, not a heading. */
export function GuideKeyTakeaways({ items }: { items: ReactNode[] }) {
  return (
    <>
      <p className="label-tag">Key takeaways</p>
      <ul>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </>
  );
}
